/**
 * ComplianceAudit (CUR-168 file-audit POC ported from project-vantage).
 *
 * Status / kind / role / state enums live as native Prisma enums and are exported
 * from `@shared/prisma/enums` (auto-generated by `prisma:generate`). This file
 * only carries the JSON-shape contracts that cannot be modelled as Prisma enums.
 */

import {
    ComplianceAuditStatus,
    ComplianceAuditStepStatus,
    ComplianceAuditSubStepStatus,
    EvidenceRetrievalMode,
} from '@shared/prisma/enums';
import { z } from 'zod';

// One location a cited passage was found at: a source filename (`File.name`,
// byte-exact), one of the two verified sentinels — the literal `Auditor (chat)`
// for the auditor's own attested words, or `Email header: <filename>` (the
// `EMAIL_HEADER_EVIDENCE_SOURCE_PREFIX` constant followed by a corpus email's
// display name, CUR-805) for exactly one clause of that email's rendered
// email-context line — or the literal `(source not verified)` marker for a
// citation the evidence guard demoted; plus the resolved page where one was
// found (never for a sentinel). `page` lives here — not on the citation —
// because it belongs to a specific file location, and the same passage can sit
// on different pages of different files.
export const complianceAuditEvidenceSourceSchema = z.object({
    source: z.string(),
    page: z.number().optional(),
});

export type ComplianceAuditEvidenceSource = z.infer<typeof complianceAuditEvidenceSourceSchema>;

// Single evidence citation — one item of the list stored on
// `ComplianceAuditSubStep.evidences` (Prisma `Json?`). A citation is one
// DISTINCT passage (CUR-841): the quoted `text` appears once, and every
// location it was found at — across files and within one file alike — sits in
// its `sources` list, in first-appearance order. The write path merges
// citations whose texts match under normalisation before persisting, so the
// flat one-source-per-citation shape (`{ text, source, page? }`) no longer
// exists in the column; legacy rows are rewritten by a db-migration, not
// tolerated here. Schemas are the single source of truth — the types are
// inferred and the parse helpers let call sites validate the untrusted JSON
// instead of `as`-casting it.
//
// `sources` is bounded `.min(1).max(8)`: a citation with no location is
// meaningless, and a merged citation can never hold more sources than the
// pre-merge citation count, which `MAX_EVIDENCE_CITATIONS` caps at 8.
export const complianceAuditEvidenceSnippetSchema = z.object({
    text: z.string(),
    sources: z.array(complianceAuditEvidenceSourceSchema).min(1).max(8),
});

export type ComplianceAuditEvidenceSnippet = z.infer<typeof complianceAuditEvidenceSnippetSchema>;

// Persisted column shape (CUR-670/CUR-671 multi-evidence, widened by CUR-731
// full document scan, grouped by CUR-841): 0–8 citations, one per distinct
// passage, with no rule about which file each one comes from — two DIFFERENT
// passages of the same file are two citations, while the SAME passage found in
// several files (or on several pages of one file) is one citation with several
// `sources` entries.
//
// This is a READ parser, so its bounds cover every write path rather than one
// mode's private contract. Bounds tighter than the column can hold do not
// truncate — the parse fails and the WHOLE citation list disappears on read.
//
// `.max(8)` is kept in lockstep with `MAX_EVIDENCE_CITATIONS` in
// `server/src/services/domain/complianceAudit/ComplianceAuditAgentService.ts`,
// which caps the guarded list on the way in; the two must move together. The
// model-facing output contracts keep the FLAT per-citation shape — grouping is
// a persistence concern applied after the evidence guard, not a wire format.
export const complianceAuditEvidenceSnippetsSchema = z.array(complianceAuditEvidenceSnippetSchema).min(0).max(8);

export type ComplianceAuditEvidenceSnippets = z.infer<typeof complianceAuditEvidenceSnippetsSchema>;

/**
 * Parse an untrusted single-citation JSON value into a typed snippet. Returns
 * null when the value is absent or doesn't match the contract, so call sites
 * render defensively rather than asserting a shape the database may not hold.
 */
export function parseComplianceAuditEvidenceSnippet(value: unknown): ComplianceAuditEvidenceSnippet | null {
    const result = complianceAuditEvidenceSnippetSchema.safeParse(value);
    return result.success ? result.data : null;
}

/**
 * Parse an untrusted `evidences` JSON value (the persisted column shape) into
 * a typed 0–8 item citation list. Returns null when the value is absent or
 * doesn't match the contract, so call sites render defensively rather than
 * asserting a shape the database may not hold. An empty array is a valid parse
 * result rather than null — the write path persists "nothing quotable" as null,
 * but a legacy or hand-edited `[]` row still reads as an empty list.
 */
export function parseComplianceAuditEvidenceSnippets(value: unknown): ComplianceAuditEvidenceSnippets | null {
    const result = complianceAuditEvidenceSnippetsSchema.safeParse(value);
    return result.success ? result.data : null;
}

// ── Trace skeleton (CUR-778) ────────────────────────────────────────────────
//
// Persisted shape of `ComplianceAuditActivity.metadata` (Prisma `Json?`). It
// holds POINTERS AND STRUCTURAL FACTS ONLY — never prompt text, chunk text or
// per-file finding content. The bodies live in `LlmRequestLog`, and the ONLY
// link between a skeleton row and its calls is `execTag`: the log row is
// inserted asynchronously by a background job, so its id does not exist when
// the trace row is written and can never be recorded here.
//
// Same read-parser discipline as `complianceAuditEvidenceSnippetsSchema` above:
// bounds must be the union of what every write path can produce, because a
// bound tighter than the column can hold does not truncate — the parse fails
// and the WHOLE trace row disappears on read. Hence no list caps (a scan-mode
// corpus is as large as the org's file set) and no `execTag` format regex;
// only "an identifier is not empty" and "a count is a non-negative integer",
// neither of which any correct write path can violate.

/**
 * How an execution was entered — a first run, a resume after the auditor
 * answered a query, or the recovery of a run the process abandoned mid-flight.
 * Mirrors `EnginePosition['kind']` in `ComplianceAuditEngine`.
 */
export const COMPLIANCE_AUDIT_EXECUTION_KINDS = ['fresh', 'resume', 'recover'] as const;

export type ComplianceAuditExecutionKind = (typeof COMPLIANCE_AUDIT_EXECUTION_KINDS)[number];

/**
 * What one file contributed to a `FULL_DOCUMENT_SCAN` sub-step: it reported
 * something, it was read and said nothing, or it could not contribute at all.
 * Matches the three corpus groups `strategy/full-document-scan.ts` builds.
 */
export const COMPLIANCE_AUDIT_PER_FILE_SCAN_OUTCOMES = ['found', 'silent', 'unavailable'] as const;

export type ComplianceAuditPerFileScanOutcome = (typeof COMPLIANCE_AUDIT_PER_FILE_SCAN_OUTCOMES)[number];

// `exec:step:<uuid>` / `exec:substep:<uuid>` — the ambient tag stamped on every
// `LlmRequestLog` row the execution produced. Two namespaces on purpose: a scan
// call sits inside both a step and a sub-step scope.
const execTagSchema = z.string().min(1);

// Bigint ids cross a Json column as strings (a `ComplianceAuditSubStep` /
// `File` id would lose precision as a JSON number).
const traceEntityIdSchema = z.string().min(1);

// Attempts, tokens and durations are counts: a no-LLM resolution (seeded,
// pre-classified, routed-out `NOT_APPLICABLE`) legitimately reports zero.
const traceCountSchema = z.number().int().nonnegative();

const complianceAuditTraceRetrievalSchema = z.object({
    query: z.string(),
    topK: traceCountSchema,
    rerankApplied: z.boolean(),
});

export type ComplianceAuditTraceRetrieval = z.infer<typeof complianceAuditTraceRetrievalSchema>;

// One retrieved chunk, WITHOUT its text: which file it came from, which
// fragment of that file (null when the file was not fragmented), how big it was
// and what the reranker scored it. Rerank scores are model-defined and are not
// bounded to 0..1.
const complianceAuditTraceChunkSchema = z.object({
    fileId: traceEntityIdSchema,
    fileName: z.string(),
    fragment: z.number().int().nullable(),
    tokens: traceCountSchema,
    rerankScore: z.number().optional(),
});

export type ComplianceAuditTraceChunk = z.infer<typeof complianceAuditTraceChunkSchema>;

// One file carrying its structural OUTCOME rather than its finding. Scan mode
// records complete coverage; other modes use the same shape for documents a
// deterministic evidence guard deliberately withheld. The entry
// resolves to its own 1..N calls by intersecting `entity:complianceAuditFile:<fileId>`
// with the execution's `execTag` — the only shape that survives a file chunked
// into several calls.
const complianceAuditTracePerFileScanSchema = z.object({
    fileId: traceEntityIdSchema,
    fileName: z.string(),
    outcome: z.enum(COMPLIANCE_AUDIT_PER_FILE_SCAN_OUTCOMES),
    reason: z.string().optional(),
    // CUR-893 — this file's final outcome came from the absence-verification
    // re-scan, not the first round. Without the marker a rescued flip is
    // indistinguishable in the trace from a first-round find, which destroys
    // exactly the forensics the incident was solved with. Only ever written as
    // true, and only on a `found` outcome.
    flippedByVerification: z.boolean().optional(),
});

export type ComplianceAuditTracePerFileScan = z.infer<typeof complianceAuditTracePerFileScanSchema>;

// CUR-893 — one absence-verification round's summary: how many silent files the
// round re-read, how many returned findings, and whether the decision that
// stands actually read them. Presence of the object on a finish row IS the "a
// round ran" fact; the counts are structural, and a zero-flip round is recorded
// too (that is the round doing its job — the absence claim now stands on a
// targeted re-read). A round that re-read NOTHING is not recorded at all: the
// re-scan's corpus/report misalignment short-circuit opens no document, and
// neither does a round whose every call failed — activity worth a warning, not
// a re-read worth reporting to an auditor.
const complianceAuditTraceVerificationRoundSchema = z.object({
    // Files the round actually re-read, net of failed re-scan calls: a file
    // whose call failed keeps its round-1 silent outcome, so counting it here
    // would put a document nobody opened behind an absence claim.
    reScannedFileCount: traceCountSchema,
    flippedFileCount: traceCountSchema,
    // Whether the flips this round recovered reached the decision that stands.
    // False in every case where they did not:
    //
    //  - the degraded case — files flipped, but the merged aggregation input
    //    could not be rendered inside its token budget, so the sub-step kept
    //    round 1's decision;
    //  - the silently-evicted case — the merged input DID render, but round 1's
    //    findings are pinned through that second budgeting pass, so a flip is
    //    what a corpus already at the budget drops (into the could-not-be-used
    //    group, with no error). The repeated decision then never saw it;
    //  - an ERROR finish row — the round ran and was billed, but the execution
    //    threw before any outcome was produced, so there is no decision for the
    //    recovered evidence to have reached. A round only ever appears on such a
    //    row through the strategy's in-flight publication, which marks it
    //    not-applied for precisely this reason;
    //  - a superseded execution — classification finished, but the
    //    generation-fenced write lost to a reassessment, so no outcome was
    //    stored for the flips to stand behind.
    //
    // Those flips are then a forensic record of what a re-read would have found
    // — NOT evidence a verdict rests on. Without the marker the trace drawer and
    // the statistics overlay report them as "recovered by verification",
    // over-reporting exactly the metric that exists to judge whether the round
    // earns its cost. True when nothing flipped on a completed decision: there
    // was nothing to merge, and the decision already reflects the re-read that
    // confirmed the silence.
    appliedToDecision: z.boolean(),
});

export type ComplianceAuditTraceVerificationRound = z.infer<typeof complianceAuditTraceVerificationRoundSchema>;

// A sub-step execution resolves to one of the sub-step's own statuses or to
// ERROR, which is a technical failure of the execution rather than a verdict —
// `ComplianceAuditSubStepStatus` has no such member, and a failure is
// deliberately a FINISHED event rather than a separate action.
const complianceAuditSubStepTraceStatusSchema = z.union([z.enum(ComplianceAuditSubStepStatus), z.literal('ERROR')]);

export type ComplianceAuditSubStepTraceStatus = z.infer<typeof complianceAuditSubStepTraceStatusSchema>;

const stepStartedTraceSchema = z.object({
    action: z.literal('STEP_STARTED'),
    execTag: execTagSchema,
    executionKind: z.enum(COMPLIANCE_AUDIT_EXECUTION_KINDS),
});

const stepFinishedTraceSchema = z.object({
    action: z.literal('STEP_FINISHED'),
    execTag: execTagSchema,
    // The step's own status enum already carries ERROR, unlike the sub-step's.
    status: z.enum(ComplianceAuditStepStatus),
    // `LlmRequestLog` is inserted asynchronously, so a recent finish row can
    // temporarily have no matching call. Persist whether the step decision
    // reached the model boundary so readers can distinguish that queue gap
    // from a deterministic, call-free resolution. Optional keeps trace rows
    // written by the first version of CUR-778 readable.
    modelCallAttempted: z.boolean().optional(),
    durationMs: traceCountSchema,
});

const subStepStartedTraceSchema = z.object({
    action: z.literal('SUB_STEP_STARTED'),
    subStepId: traceEntityIdSchema,
    execTag: execTagSchema,
    executionKind: z.enum(COMPLIANCE_AUDIT_EXECUTION_KINDS),
    mode: z.enum(EvidenceRetrievalMode),
});

const subStepFinishedTraceSchema = z.object({
    action: z.literal('SUB_STEP_FINISHED'),
    subStepId: traceEntityIdSchema,
    execTag: execTagSchema,
    status: complianceAuditSubStepTraceStatusSchema,
    // The failure's CLASS, never its message.
    //
    // A provider failure string can quote the prompt that caused it —
    // content-filter refusals echo the offending text, AI SDK errors embed
    // the response body — and THIS row is permanent, while the same text on
    // the `LlmRequestLog` row is deleted when the audit retention window
    // passes. Storing the message here would therefore give free provider
    // text a home that outlives the retention policy governing it, and
    // pattern-based redaction on the way out does not remove the value at
    // rest. A constructor name is a code identifier and can never carry
    // tenant content; the message itself stays on the retention-managed call
    // row the drawer lists directly underneath.
    //
    // Written alongside `status: 'ERROR'`; the pairing is deliberately not
    // enforced here, so a row that carries a name next to some other status
    // still reads rather than vanishing.
    errorName: z.string().optional(),
    attempts: traceCountSchema,
    model: z.string().optional(),
    totalTokens: traceCountSchema.optional(),
    durationMs: traceCountSchema,
    classifiedValue: z.string().optional(),
    retrieval: complianceAuditTraceRetrievalSchema.optional(),
    chunks: z.array(complianceAuditTraceChunkSchema).optional(),
    perFileScans: z.array(complianceAuditTracePerFileScanSchema).optional(),
    // CUR-893 — present exactly when the sub-step ran an absence-verification
    // round; absent rows (every pre-change row included) read as "no round ran".
    verificationRound: complianceAuditTraceVerificationRoundSchema.optional(),
});

/**
 * The four trace actions, discriminated by `action`. `ComplianceAuditActivity.kind`
 * is DERIVED from this discriminant and never supplied separately, so the two
 * cannot disagree. Step events keep the existing `SYSTEM_STARTED` /
 * `SYSTEM_DECIDED` kinds; `action` is what readers switch on.
 *
 * A `*_STARTED` row is written before the LLM call and outside any transaction,
 * so a start with no matching finish is the durable signal of a crashed
 * execution rather than a hole in the trace.
 */
export const complianceAuditTraceMetadataSchema = z.discriminatedUnion('action', [
    stepStartedTraceSchema,
    stepFinishedTraceSchema,
    subStepStartedTraceSchema,
    subStepFinishedTraceSchema,
]);

export type ComplianceAuditTraceMetadata = z.infer<typeof complianceAuditTraceMetadataSchema>;

// Step-conflict detection: the decision cross-checked this step's sub-step
// findings and found a contradiction. `material` records whether resolving it
// would change the verdict (a material conflict halts the step as QUERY). The
// contradiction's DESCRIPTION lives in the row's `message` (the decision's
// neutral `conflictSummary`), not here — metadata stays structural facts only.
const conflictDetectedMetadataSchema = z.object({
    action: z.literal('CONFLICT_DETECTED'),
    material: z.boolean(),
});

// Step flag (CUR-547): the step decision — the decision prompt on a deciding
// step, the routing-step prompt on a routing step — raised a flag for review.
// The flag REASON lives in the row's `message`; metadata carries only the
// action, from which the kind derives.
const flagRaisedMetadataSchema = z.object({
    action: z.literal('FLAG_RAISED'),
});

// ── Self-correct sub-steps (CUR-705 / CUR-1691) ─────────────────────────────

/**
 * How a self-correct conflict is settled: `FINDINGS` — a finding is unsupported
 * or contradicted, so re-reading the documents can settle it (the step's
 * sub-steps re-run); `DOCUMENTS` — verified passages of different documents
 * genuinely disagree, which only the step decision (and the auditor) can settle.
 */
export const COMPLIANCE_AUDIT_SELF_CORRECTION_CONFLICT_KINDS = ['FINDINGS', 'DOCUMENTS'] as const;

export type ComplianceAuditSelfCorrectionConflictKind =
    (typeof COMPLIANCE_AUDIT_SELF_CORRECTION_CONFLICT_KINDS)[number];

/**
 * Why a `SYSTEM_SELF_CORRECTION` row was written: the phase re-ran the step's
 * sub-steps, or the check could not run and the step proceeded on its first
 * pass (fail-open). A clean check, or one with only `DOCUMENTS` conflicts,
 * writes no row.
 */
export const COMPLIANCE_AUDIT_SELF_CORRECTION_OUTCOMES = ['RERUN', 'FAILED'] as const;

export type ComplianceAuditSelfCorrectionOutcome = (typeof COMPLIANCE_AUDIT_SELF_CORRECTION_OUTCOMES)[number];

const selfCorrectionConflictSchema = z.object({
    kind: z.enum(COMPLIANCE_AUDIT_SELF_CORRECTION_CONFLICT_KINDS),
    subStepLabels: z.array(z.string()),
    summary: z.string(),
});

export type ComplianceAuditSelfCorrectionConflict = z.infer<typeof selfCorrectionConflictSchema>;

const selfCorrectionHintSchema = z.object({
    subStepLabel: z.string(),
    hint: z.string(),
});

export type ComplianceAuditSelfCorrectionHint = z.infer<typeof selfCorrectionHintSchema>;

// One sub-step as the first pass left it, copied from its row before the
// re-run's reset clears it. `evidences` is the persisted column value verbatim,
// so it shares that column's read parser and bounds.
const selfCorrectionSubStepSnapshotSchema = z.object({
    subStepId: traceEntityIdSchema,
    label: z.string(),
    status: z.enum(ComplianceAuditSubStepStatus),
    finding: z.string().nullable(),
    classifiedValue: z.string().nullable(),
    evidences: complianceAuditEvidenceSnippetsSchema.nullable(),
});

export type ComplianceAuditSelfCorrectionSubStepSnapshot = z.infer<typeof selfCorrectionSubStepSnapshotSchema>;

// The step-scoped record of one self-correct cycle. Unlike every other action
// here, this metadata deliberately holds tenant content — the conflicts, the
// model's hints and the first-pass findings and citations — because the re-run
// overwrites the sub-step rows and this row is the only place the first pass
// stays inspectable. It is the same tenancy and content class as the sub-step
// `finding` column, never logged, never sent to telemetry and never part of the
// trace readers (they parse trace actions only). The neutral summary lives in
// the row's `message`.
//
// Read-parser bounds as above: no list caps. A `FAILED` row carries empty
// `conflicts` and `hints` and the failure's class in `errorName`, never its
// message (see `SUB_STEP_FINISHED`).
const selfCorrectionMetadataSchema = z.object({
    action: z.literal('SELF_CORRECTION'),
    outcome: z.enum(COMPLIANCE_AUDIT_SELF_CORRECTION_OUTCOMES),
    conflicts: z.array(selfCorrectionConflictSchema),
    hints: z.array(selfCorrectionHintSchema),
    firstPass: z.array(selfCorrectionSubStepSnapshotSchema),
    errorName: z.string().optional(),
});

export type ComplianceAuditSelfCorrectionMetadata = z.infer<typeof selfCorrectionMetadataSchema>;

/**
 * Every persisted `ComplianceAuditActivity.metadata` shape: the four CUR-778
 * trace actions plus the step-conflict, step-flag and self-correction records.
 * Kind derivation from `action` covers all of them (see
 * `logComplianceAuditActivity`).
 */
export const complianceAuditActivityMetadataSchema = z.discriminatedUnion('action', [
    stepStartedTraceSchema,
    stepFinishedTraceSchema,
    subStepStartedTraceSchema,
    subStepFinishedTraceSchema,
    conflictDetectedMetadataSchema,
    flagRaisedMetadataSchema,
    selfCorrectionMetadataSchema,
]);

export type ComplianceAuditActivityMetadata = z.infer<typeof complianceAuditActivityMetadataSchema>;

/**
 * Parse an untrusted `metadata` JSON value into a typed payload. Returns
 * null for everything that is not one of the current actions — an absent
 * column (every user-action row writes `metadata = null`), a legacy or
 * hand-edited shape, a JSON *string* that was never decoded, or an action a
 * future writer added that this reader does not know. Metadata rows are
 * diagnostic: a row that cannot be understood is skipped, never thrown on.
 */
export function parseComplianceAuditActivityMetadata(value: unknown): ComplianceAuditActivityMetadata | null {
    const result = complianceAuditActivityMetadataSchema.safeParse(value);
    return result.success ? result.data : null;
}

/**
 * Trace-only variant of {@link parseComplianceAuditActivityMetadata}: the trace
 * readers fold `*_STARTED`/`*_FINISHED` pairs into executions and must never
 * see a `CONFLICT_DETECTED`, `FLAG_RAISED` or `SELF_CORRECTION` row (none has an `execTag` to fold on). Same
 * defensive contract — anything else parses to null and is skipped.
 */
export function parseComplianceAuditTraceMetadata(value: unknown): ComplianceAuditTraceMetadata | null {
    const result = complianceAuditTraceMetadataSchema.safeParse(value);
    return result.success ? result.data : null;
}

/**
 * Derive the aggregate `ComplianceAudit.status` from its steps. Single source of
 * truth for the audit-level status so the list view (persisted `status`), the
 * detail header (`StatusBadge`), the insights pass rate and the MCP outcome read
 * never disagree. In-flight states win, then any failed step fails the audit
 * (CUR-1624):
 *
 * | steps                                            | audit       |
 * |--------------------------------------------------|-------------|
 * | any step ERROR                                   | ERROR       |
 * | any step WAITING or QUERIED                      | WAITING     |
 * | any step PENDING, or some but not all resolved   | PENDING     |
 * | no step resolved                                 | NOT_STARTED |
 * | all resolved and at least one FAILED             | FAILED      |
 * | all resolved and no FAILED                       | PASSED      |
 *
 * Resolved = PASSED, FAILED or NOT_APPLICABLE (a routed-out step; CUR-551).
 * ERROR dominates because a technical failure must be visible in the list;
 * WAITING covers both a step held open by a queried sub-step and a step whose
 * decision itself asked, since either way the run cannot move until the auditor
 * answers or overrides. A FAILED step never pre-empts an open step: the audit
 * reads WAITING or PENDING until every step is resolved, because the auditor's
 * answer or override can still flip that failure.
 *
 * A NOT_APPLICABLE step counts as resolved (it is done) but takes no part in the
 * verdict — it neither fails the audit nor rescues it — so an all-not-applicable
 * audit derives PASSED and carries the "no step assessed" qualifier instead of a
 * separate status.
 */
export function deriveComplianceAuditStatus(
    steps: readonly { status: ComplianceAuditStepStatus }[],
): ComplianceAuditStatus {
    // A permanent technical failure on any step (CUR-168 §4.4) surfaces at the
    // audit level above all other states so it is visible in the list view.
    if (steps.some((step) => step.status === ComplianceAuditStepStatus.ERROR)) {
        return ComplianceAuditStatus.ERROR;
    }
    // A question at either level halts the chain for the auditor: WAITING is a
    // step held open by a queried sub-step, QUERIED is the step decision itself
    // asking. Both read as WAITING to the auditor — there is nothing to do but
    // answer or override.
    if (
        steps.some(
            (step) =>
                step.status === ComplianceAuditStepStatus.WAITING || step.status === ComplianceAuditStepStatus.QUERIED,
        )
    ) {
        return ComplianceAuditStatus.WAITING;
    }
    if (steps.some((step) => step.status === ComplianceAuditStepStatus.PENDING)) {
        return ComplianceAuditStatus.PENDING;
    }
    const resolvedCount = steps.filter(
        (step) =>
            step.status === ComplianceAuditStepStatus.PASSED ||
            step.status === ComplianceAuditStepStatus.FAILED ||
            step.status === ComplianceAuditStepStatus.NOT_APPLICABLE,
    ).length;
    if (resolvedCount === 0) {
        return ComplianceAuditStatus.NOT_STARTED;
    }
    if (resolvedCount !== steps.length) {
        return ComplianceAuditStatus.PENDING;
    }
    // Every step is resolved: the audit carries its verdict. NOT_APPLICABLE steps
    // are ignored here, so only an actual FAILED step can fail the audit.
    return steps.some((step) => step.status === ComplianceAuditStepStatus.FAILED)
        ? ComplianceAuditStatus.FAILED
        : ComplianceAuditStatus.PASSED;
}
